'use client';

/**
 * Material Tailwind compatibility adapter for the main fe-services app.
 *
 * Why this file exists:
 * - During React 19 migration, parts of `@material-tailwind/react` typings can
 *   resolve to overly strict prop contracts in consumer code.
 * - Typical symptom: components like `Button`, `Dialog`, or `IconButton`
 *   report impossible required props (`placeholder`, `onResize`,
 *   `onPointerEnterCapture`, etc.) that are not required by the intended
 *   runtime API.
 *
 * What this adapter does:
 * - Exposes a stable import surface via the existing tsconfig path alias
 *   (`@material-tailwind/react` -> `src/modules/material-tailwind`).
 * - Applies focused type-level compatibility casts (`as unknown as ...`) only
 *   where React 19 + upstream typings mismatch is known.
 * - Keeps these migration workarounds centralized, instead of spreading local
 *   casts across feature modules.
 *
 * Guardrails:
 * - This is a migration bridge, not a design-system abstraction.
 * - Add new wrappers only when direct imports are proven to fail due to the
 *   same typing issue.
 * - Prefer direct library typings where they compile correctly.
 *
 * Removal criteria:
 * - Upstream Material Tailwind typings become fully React 19 compatible for our
 *   usage patterns, and
 * - all current consumers type-check with direct imports and no compatibility
 *   casts.
 */

export type {
  ButtonStyleTypes,
  CardBodyStylesType,
  CardFooterStylesType,
  CardStylesType,
  CheckboxStylesType,
  DialogStylesType,
  InputStylesType,
  PopoverProps,
  SelectStylesType,
  TextareaStylesType,
  TooltipProps,
} from '@material-tailwind/react/index';

import type { ButtonProps } from '@material-tailwind/react/components/Button';
import type {
  CardBodyProps,
  CardFooterProps,
  CardProps,
} from '@material-tailwind/react/components/Card';
import type { CheckboxProps } from '@material-tailwind/react/components/Checkbox';
import type {
  DialogBodyProps,
  DialogFooterProps,
  DialogHeaderProps,
  DialogProps,
} from '@material-tailwind/react/components/Dialog';
import type { DrawerProps } from '@material-tailwind/react/components/Drawer';
import type { IconButtonProps } from '@material-tailwind/react/components/IconButton';
import type { InputProps } from '@material-tailwind/react/components/Input';
import type {
  MenuHandlerProps,
  MenuItemProps,
  MenuListProps,
  MenuProps,
} from '@material-tailwind/react/components/Menu';
import type {
  PopoverContentProps,
  PopoverHandlerProps,
} from '@material-tailwind/react/components/Popover';
import type { RadioProps } from '@material-tailwind/react/components/Radio';
import type { SelectProps } from '@material-tailwind/react/components/Select';
import type {
  TabProps,
  TabsHeaderProps,
  TabsProps,
} from '@material-tailwind/react/components/Tabs';
import type { TextareaProps } from '@material-tailwind/react/components/Textarea';
import type { TypographyProps } from '@material-tailwind/react/components/Typography';
import { MaterialTailwindTheme } from '@material-tailwind/react/context/theme';
import * as MT from '@material-tailwind/react/index';
import defaultTheme from '@material-tailwind/react/theme/index';
// eslint-disable-next-line import/order
import {
  createElement,
  type ComponentType,
  type ForwardRefExoticComponent,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
} from 'react';

type ForwardRefComponent<P, R> = ForwardRefExoticComponent<
  P & RefAttributes<R>
>;

type PatchedColor = 'primary' | 'transparent' | 'neutral' | 'black';
type WithPatchedColor<P> = P extends { color?: infer C }
  ? Omit<P, 'color'> & { color?: C | PatchedColor }
  : P;
type WithOptionalChildren<P> = Omit<P, 'children'> & { children?: ReactNode };
type CompatComponent<P> = ComponentType<WithOptionalChildren<P>>;

import { mergeTheme } from './mergeTheme';

export const Collapse = MT.Collapse;
export const Option = MT.Option;
export const Tooltip = MT.Tooltip;

/**
 * Safe replacement for MT.ThemeProvider that avoids deepmerge infinite recursion with React 19.
 * deepmerge doesn't recognize Symbol(react.transitional.element) and tries to merge React internals.
 */
export function ThemeProvider({
  value,
  children,
}: {
  value: object;
  children: ReactNode;
}): ReactElement {
  const merged = mergeTheme(defaultTheme, value);
  return createElement(
    MaterialTailwindTheme.Provider,
    { value: merged },
    children,
  );
}

export const Button = MT.Button as unknown as CompatComponent<
  WithPatchedColor<ButtonProps>
>;
export const Checkbox =
  MT.Checkbox as unknown as CompatComponent<CheckboxProps>;

export const Dialog = MT.Dialog as unknown as ForwardRefComponent<
  DialogProps,
  HTMLDivElement
>;
export const DialogHeader = MT.DialogHeader as unknown as ForwardRefComponent<
  DialogHeaderProps,
  HTMLDivElement
>;
export const DialogBody = MT.DialogBody as unknown as ForwardRefComponent<
  DialogBodyProps,
  HTMLDivElement
>;
export const DialogFooter = MT.DialogFooter as unknown as ForwardRefComponent<
  DialogFooterProps,
  HTMLDivElement
>;

export const Drawer = MT.Drawer as unknown as ForwardRefComponent<
  DrawerProps,
  HTMLDivElement
>;

export const IconButton = MT.IconButton as unknown as CompatComponent<
  WithPatchedColor<IconButtonProps>
>;

export const Input = MT.Input as unknown as ForwardRefComponent<
  InputProps,
  HTMLInputElement
>;

export const Popover = MT.Popover as unknown as typeof MT.Popover & {
  Handler: ForwardRefComponent<PopoverHandlerProps, HTMLDivElement>;
  Content: ForwardRefComponent<PopoverContentProps, HTMLDivElement>;
};
export const PopoverHandler =
  MT.PopoverHandler as unknown as ForwardRefComponent<
    PopoverHandlerProps,
    HTMLDivElement
  >;
export const PopoverContent =
  MT.PopoverContent as unknown as ForwardRefComponent<
    PopoverContentProps,
    HTMLDivElement
  >;

export const Card = MT.Card as unknown as ForwardRefComponent<
  CardProps,
  HTMLDivElement
> & {
  Body: ForwardRefComponent<CardBodyProps, HTMLDivElement>;
  Footer: ForwardRefComponent<CardFooterProps, HTMLDivElement>;
};
export const CardBody = MT.CardBody as unknown as ForwardRefComponent<
  CardBodyProps,
  HTMLDivElement
>;
export const CardFooter = MT.CardFooter as unknown as ForwardRefComponent<
  CardFooterProps,
  HTMLDivElement
>;

export const Menu = MT.Menu as unknown as ForwardRefComponent<
  MenuProps,
  HTMLButtonElement
> & {
  Handler: ForwardRefComponent<MenuHandlerProps, HTMLButtonElement>;
  List: ForwardRefComponent<MenuListProps, HTMLUListElement>;
  Item: ForwardRefComponent<MenuItemProps, HTMLButtonElement>;
};
export const MenuHandler = MT.MenuHandler as unknown as ForwardRefComponent<
  MenuHandlerProps,
  HTMLButtonElement
>;
export const MenuList = MT.MenuList as unknown as ForwardRefComponent<
  MenuListProps,
  HTMLUListElement
>;
export const MenuItem = MT.MenuItem as unknown as ForwardRefComponent<
  MenuItemProps,
  HTMLButtonElement
>;

export const Radio = MT.Radio as unknown as ForwardRefComponent<
  RadioProps,
  HTMLInputElement
>;

export const Select = MT.Select as unknown as ForwardRefComponent<
  SelectProps,
  HTMLDivElement
>;

export const Tabs = MT.Tabs as unknown as ForwardRefComponent<
  TabsProps,
  HTMLDivElement
>;
export const TabsHeader = MT.TabsHeader as unknown as ForwardRefComponent<
  TabsHeaderProps,
  HTMLUListElement
>;
export const Tab = MT.Tab as unknown as ForwardRefComponent<
  TabProps,
  HTMLLIElement
>;

export const Textarea = MT.Textarea as unknown as ForwardRefComponent<
  TextareaProps,
  HTMLTextAreaElement
>;

export const Typography = MT.Typography as unknown as ForwardRefComponent<
  WithPatchedColor<TypographyProps>,
  HTMLElement
>;
